export type StorageCategoryId = 'photos' | 'audio' | 'video' | 'files' | 'other';

export type StorageStat = { id: StorageCategoryId; label: string; size: number };

const offlineKey = (uid: number, suffix: string) => `u:${uid}:${suffix}`;
export const directMessageDraftKey = (uid: number, peerUserId: number) => offlineKey(uid, `dm_draft_v1:${peerUserId}`);

export const offlineConversationsKey = (uid: number) => offlineKey(uid, 'conversations_v1');
export const offlineRoomsKey = (uid: number) => offlineKey(uid, 'rooms_v1');
export const offlinePendingKey = (uid: number) => offlineKey(uid, 'pending_v1');
export const offlineDmKey = (uid: number, peerUserId: number) => offlineKey(uid, `dm:${peerUserId}`);
export const offlineRoomKey = (uid: number, roomId: number) => offlineKey(uid, `room:${roomId}`);
export const offlineDmWallpaperKey = (uid: number, peerUserId: number) => offlineKey(uid, `dmwp:${peerUserId}`);
export const offlineRoomWallpaperKey = (uid: number, roomId: number) => offlineKey(uid, `roomwp:${roomId}`);
export const offlineChatMemKey = (uid: number) => offlineKey(uid, 'chatmap_mem_v1');
export const offlineSettingsKey = (uid: number) => offlineKey(uid, 'messenger_settings_v1');
export const offlineSyncCursorKey = (uid: number, kind: 'dm' | 'room', targetId: number) =>
  offlineKey(uid, `sync_cursor:${kind}:${targetId}`);
export const offlineChatHashKey = (uid: number, kind: 'dm' | 'room', targetId: number) =>
  offlineKey(uid, `chat_hash:${kind}:${targetId}`);
export const offlineChatListHashKey = (uid: number) => offlineKey(uid, 'chat_list_hash_v1');

export function formatBytesShort(bytes: number): string {
  if (!(bytes > 0)) return '0 Б';
  const units = ['Б', 'КБ', 'МБ', 'ГБ'];
  const idx = Math.min(units.length - 1, Math.floor(Math.log(bytes) / Math.log(1024)));
  const value = bytes / 1024 ** idx;
  return `${value >= 10 || value % 1 === 0 ? value.toFixed(0) : value.toFixed(1)} ${units[idx]}`;
}

export function detectStorageKind(url: string, contentType: string): StorageCategoryId {
  const normalizedUrl = url.toLowerCase();
  const normalizedContentType = contentType.toLowerCase();

  if (normalizedContentType.startsWith('image/') || /\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/.test(normalizedUrl)) {
    return 'photos';
  }
  if (
    normalizedContentType.startsWith('audio/') ||
    /\/chat_voice\//.test(normalizedUrl) ||
    /\.(opus|webm|m4a|mp3|wav)(\?|$)/.test(normalizedUrl)
  ) {
    return 'audio';
  }
  if (normalizedContentType.startsWith('video/') || /\.(mp4|mov|mkv)(\?|$)/.test(normalizedUrl)) {
    return 'video';
  }
  if (/\.(pdf|doc|docx|xls|xlsx|zip|rar|7z|csv|txt)(\?|$)/.test(normalizedUrl)) {
    return 'files';
  }
  return 'other';
}
